import { Queue } from './mps.models';
import { DeepPascalCase } from '../utils/types';

export enum SignalRNotificationType {
    Ecg = 0,
    Mps = 1,
    Association = 2,
    Worklist = 3,
    SISS3 = 4,
    Sr = 5,
    Store = 6,
}

export enum AssociationType {
    Ecg = 0,
    DDevice = 1,
    Sr = 2,
}

export enum AlarmSeverity {
    Alert = 0,
    StopAlert = 1,
    TemporizedWarning = 2,
    ExamApproved = 3,
}

export interface BaseNotification {
    notificationType: SignalRNotificationType;
    idUsers?: string[] | null;
}

export interface EcgNotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Ecg;
    severityType: AlarmSeverity;
    idExamination: string;
    examinationDate: string;
    patient: EcgNotificationPatient;
}

export interface EcgNotificationPatient {
    id: string;
    firstName: string | null;
    lastName: string | null;
    sex: string | null;
    sexCode: string | null;
    taxCode: string | null;
    birthDate?: string | null;
    race: string | null;
    patientId: string | null;
    issuer: string | null;
    otherPatientId: string | null;
    isLocalPatient: boolean;
    isCertificating: boolean | null;
    idLocalPatient: string | null;
    residenceCity: string | null;
}

export enum EcgAlarmAction {
    WarningAccepted = 0,
    ExamRemoved = 1,
}

export interface MpsNotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Mps;
    id: number;
    queue: Queue;
    delete: boolean;
    add: boolean;
}

export interface AssociationNotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Association;
    idExamination: string;
    associationType: AssociationType;
}

export enum WorklistNotificationFormType {
    Ecg = 0,
    Execution = 1,
    Reporting = 2,
}

export interface WorklistNotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Worklist;
    formType: WorklistNotificationFormType;
    eventDate: string;
    idBusinessUnit?: string | null;
    idUser?: string | null;
}

export interface WorklistEcgNotification extends WorklistNotification {
    readonly formType: WorklistNotificationFormType.Ecg;
}

export interface WorklistExecutionNotification extends WorklistNotification {
    readonly formType: WorklistNotificationFormType.Execution;
}

export interface WorklistReportingNotification extends WorklistNotification {
    readonly formType: WorklistNotificationFormType.Reporting;
}

export interface SISS3Notification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.SISS3;
    id: string | null;
    token: string | null;
    error: string | null;
    timestamp: string | null;
}

export interface US2AINotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Sr;
    idRepository: string;
    studyInstanceUid: string;
}

export interface StoreNotification extends BaseNotification {
    readonly notificationType: SignalRNotificationType.Store;
    idExamination: string;
}

// Per semplificare i log.
export const notificationTypeToString: Record<SignalRNotificationType, string> = {
    [SignalRNotificationType.Association]: 'Association',
    [SignalRNotificationType.Ecg]: 'ECG',
    [SignalRNotificationType.Mps]: 'MPS',
    [SignalRNotificationType.Worklist]: 'Worklist',
    [SignalRNotificationType.SISS3]: 'SISSWay',
    [SignalRNotificationType.Sr]: 'Sr',
    [SignalRNotificationType.Store]: 'Store',
};

export const alarmSeverityToString: Record<AlarmSeverity, string> = {
    [AlarmSeverity.Alert]: 'Alert',
    [AlarmSeverity.StopAlert]: 'StopAlert',
    [AlarmSeverity.TemporizedWarning]: 'TemporizedWarning',
    [AlarmSeverity.ExamApproved]: 'ExamApproved',
};

export type Notification =
    | EcgNotification
    | MpsNotification
    | AssociationNotification
    | WorklistNotification
    | SISS3Notification
    | US2AINotification
    | StoreNotification;

export function isEcgNotification(notification: Notification): notification is EcgNotification {
    return notification.notificationType === SignalRNotificationType.Ecg;
}

export function isMpsNotification(notification: Notification): notification is MpsNotification {
    return notification.notificationType === SignalRNotificationType.Mps;
}

export function isAssociationNotification(notification: Notification): notification is AssociationNotification {
    return notification.notificationType === SignalRNotificationType.Association;
}

export function isWorklistNotification(notification: Notification): notification is WorklistNotification {
    return notification.notificationType === SignalRNotificationType.Worklist;
}

export function isWorklistEcgNotification(notification: Notification): notification is WorklistEcgNotification {
    return isWorklistNotification(notification) && notification.formType === WorklistNotificationFormType.Ecg;
}

export function isWorklistExecutionNotification(
    notification: Notification,
): notification is WorklistExecutionNotification {
    return isWorklistNotification(notification) && notification.formType === WorklistNotificationFormType.Execution;
}

export function isWorklistReportingNotification(
    notification: Notification,
): notification is WorklistReportingNotification {
    return isWorklistNotification(notification) && notification.formType === WorklistNotificationFormType.Reporting;
}

export function isSISS3Notification(notification: Notification): notification is SISS3Notification {
    return notification.notificationType === SignalRNotificationType.SISS3;
}

export function isUS2AINotification(notification: Notification): notification is US2AINotification {
    return notification.notificationType === SignalRNotificationType.Sr;
}

export function isStoreNotification(notification: Notification): notification is StoreNotification {
    return notification.notificationType === SignalRNotificationType.Store;
}

export interface RestEvent {
    CorrelationId: string;
    Notification: DeepPascalCase<Notification>;
}
